import React, { useCallback, useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import {
  deleteDashboardSavedFilter,
  getDashboardSavedFilters,
  saveDashboardFilter,
  toggleDashboardSavedFilterFavorite,
} from "../../utils/dashboardSavedFilters";

/**
 * Painel "Filtros salvos" para modais do Dashboard (por seção).
 */
export default function DashboardSavedFiltersPanel({
  section,
  getSnapshot,
  onApply,
  canSave = true,
}) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const [entries, setEntries] = useState([]);
  const [saveName, setSaveName] = useState("");
  const [saveFavorite, setSaveFavorite] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(() => {
    setEntries(getDashboardSavedFilters(section));
  }, [section]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (entries.length > 0) setExpanded(true);
  }, [entries.length]);

  const handleSave = () => {
    setSaveError("");
    try {
      const state = typeof getSnapshot === "function" ? getSnapshot() : null;
      saveDashboardFilter({
        section,
        nome: saveName,
        state,
        favorito: saveFavorite,
      });
      setSaveName("");
      setSaveFavorite(false);
      setExpanded(true);
      refresh();
    } catch (err) {
      setSaveError(
        err?.message ||
          t("dashboardPage.savedFilters.saveError", {
            defaultValue: "Não foi possível salvar o filtro",
          })
      );
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((v) => !v)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.75,
          py: 1.25,
          border: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          cursor: "pointer",
          textAlign: "left",
          font: "inherit",
          color: "inherit",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.07) },
        }}
      >
        <BookmarkBorderIcon fontSize="small" color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t("dashboardPage.savedFilters.title", { defaultValue: "Meus filtros" })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {entries.length > 0
              ? t("dashboardPage.savedFilters.count", {
                  defaultValue: "{{count}} salvo(s) nesta aba",
                  count: entries.length,
                })
              : t("dashboardPage.savedFilters.emptyShortHint", {
                  defaultValue: "Guarde esta combinação para reaplicar depois",
                })}
          </Typography>
        </Box>
        {expanded ? <ExpandLessIcon color="action" /> : <ExpandMoreIcon color="action" />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 1.75, pt: 1.25, pb: 1.75 }}>
          {entries.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mb: 1.5 }}>
              {entries.map((entry) => (
                <Box
                  key={entry.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 1.5,
                    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={
                      entry.favorito
                        ? t("dashboardPage.savedFilters.unfavorite", {
                            defaultValue: "Remover favorito",
                          })
                        : t("dashboardPage.savedFilters.favorite", {
                            defaultValue: "Marcar favorito",
                          })
                    }
                    onClick={() => {
                      toggleDashboardSavedFilterFavorite(entry.id);
                      refresh();
                    }}
                  >
                    {entry.favorito ? (
                      <StarIcon fontSize="small" color="warning" />
                    ) : (
                      <StarBorderIcon fontSize="small" />
                    )}
                  </IconButton>
                  <ListItemButton
                    dense
                    onClick={() => onApply?.(entry.state)}
                    sx={{ flex: 1, borderRadius: 1.5, py: 0.35 }}
                  >
                    <ListItemText
                      primary={entry.nome}
                      secondary={t("dashboardPage.savedFilters.applyHintShort", {
                        defaultValue: "Aplicar",
                      })}
                      primaryTypographyProps={{ variant: "body2", fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ variant: "caption" }}
                    />
                  </ListItemButton>
                  <IconButton
                    size="small"
                    aria-label={t("dashboardPage.savedFilters.delete", {
                      defaultValue: "Excluir filtro salvo",
                    })}
                    onClick={() => {
                      deleteDashboardSavedFilter(entry.id);
                      refresh();
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1.5, fontSize: "0.8125rem", lineHeight: 1.4 }}
            >
              {t("dashboardPage.savedFilters.empty", {
                defaultValue:
                  "Salve combinações usadas com frequência para reaplicá-las com um clique.",
              })}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            {t("dashboardPage.savedFilters.saveCurrent", {
              defaultValue: "Salvar combinação atual",
            })}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder={t("dashboardPage.savedFilters.namePlaceholder", {
                defaultValue: "Ex.: Conversão alta",
              })}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              disabled={!canSave}
              sx={{ flex: "1 1 180px", minWidth: 140, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              <Checkbox
                size="small"
                checked={saveFavorite}
                onChange={(e) => setSaveFavorite(e.target.checked)}
                disabled={!canSave}
              />
              <Typography variant="caption" color="text.secondary">
                {t("dashboardPage.savedFilters.favoriteLabel", { defaultValue: "Favorito" })}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSave}
              disabled={!canSave || !saveName.trim()}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              {t("dashboardPage.savedFilters.save", { defaultValue: "Salvar" })}
            </Button>
          </Box>
          {saveError ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
              {saveError}
            </Typography>
          ) : null}
        </Box>
      </Collapse>
    </Box>
  );
}
